import dayjs from 'dayjs';
import cookie from 'js-cookie';
import type { ArticleV1 } from 'types/capi/article';
import type { DynamicProductBlock } from 'types/capi/content';
import type { User } from 'types/User';
import * as uuid from 'uuid';

import { getStockStatus } from '~/domain/stockStatus';
import brazeClient from '~/services/braze.client';
import facebook from '~/services/facebook';
import facebookPixel from '~/services/facebook.pixel';
import gtm from '~/services/gtm.client';
import tiktokPixel from '~/services/tiktok.pixel';
import twitter from '~/services/twitter.pixel';

import UserEvent from './UserEvent';

type EventData = {
  article: ArticleV1;
  clientIp: string | null;
  isBot: boolean;
  user: User;
};

class ViewNewsArticleEvent extends UserEvent<EventData> {
  private getArticleType() {
    return this.data.article.tracking?.click_refs?.at ?? '';
  }

  private getContentIds() {
    return this.data.article.content
      .filter((i) => i.type === 'dynamic_product_block')
      .map((i) => (i as DynamicProductBlock).product_id);
  }

  private formatToDate(timestamp: number) {
    const date = new Date(timestamp * 1000);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  }

  sendToGtm() {
    const { published, modified } = this.data.article.date;

    gtm.pushToDataLayer({
      articleType: this.getArticleType(),
      author: this.data.article.author.name,
      canonicalPath: this.data.article.canonical_path,
      event: 'viewNewsArticle',
      publishDate: this.formatToDate(published),
      updateDate: modified ? this.formatToDate(modified) : undefined
    });
  }

  async sendToFacebook() {
    const eventId = uuid.v4();

    const hasInStockDpb = this.data.article.content.some((c) => {
      if (c.type !== 'dynamic_product_block') return false;

      return (
        getStockStatus((c as DynamicProductBlock).stock_status) === 'in-stock'
      );
    });

    if (!hasInStockDpb) return;

    await facebookPixel.fireEvent({
      data: {
        content_ids: this.getContentIds(),
        content_type: 'product_group',
        currency: 'GBP'
      },
      eventID: eventId,
      eventName: facebookPixel.StandardEventNames.ViewContent
    });

    const hashedProfile = await facebook.getHashedProfile(
      this.data.user.profile
    );

    const serverEventData = [
      {
        action_source: 'website',
        custom_data: {
          content_ids: this.data.article.tracking.related_products,
          content_name: this.data.article.title,
          content_type: 'article'
        },
        event_id: eventId,
        event_name: facebookPixel.StandardEventNames.ViewContent,
        event_source_url: this.data.article.canonical_url,
        event_time: dayjs().unix(),
        user_data: {
          client_ip_address: this.data.clientIp,
          client_user_agent: navigator?.userAgent,
          external_id: this.data.user.id ?? 'guest',
          fbc: cookie.get('_fbc'),
          fbp: cookie.get('_fbp'),
          ...hashedProfile
        }
      }
    ];

    this.serverEvents.push({ data: serverEventData, type: 'facebook' });
  }

  sendToTikTok() {
    return tiktokPixel.fireEvent('ViewContent', {
      content_ids: this.getContentIds(),
      content_type: 'product',
      currency: 'GBP',
      value: 0
    });
  }

  sendToTwitter() {
    return twitter.fireEvent(twitter.EventIds.ViewContent, {
      contents: this.getContentIds().map((productId) => ({
        content_id: productId,
        content_type: 'product'
      }))
    });
  }

  sendToBraze() {
    return brazeClient.logArticleViewEvent(this.data.article.title);
  }
}

export default ViewNewsArticleEvent;
